/**
 * The gallery's device preview, as a real document.
 *
 * WHY
 *
 * The Preview modal used to mount the storefront into an `about:blank` iframe
 * from the PARENT's realm (PreviewViewportFrame). Layout resolved against the
 * frame — media queries, `vh`, sticky — but every line of the renderer's JS
 * still ran against the STUDIO's `window`: `window.scrollY` and scroll
 * listeners (the header that hides on scroll-down), `window.matchMedia` /
 * `innerWidth` (the JS half of the responsive layout), Lenis smooth-scroll and
 * parallax. So the navbar never hid and mobile/tablet had no JS responsiveness.
 * Onboarding's step 5 never had this: the dashboard loads `/preview` as its
 * own document.
 *
 * So the modal now loads `/preview?frame=device` — the Studio itself, in its
 * own realm — at the device's logical viewport.
 *
 * WHY THE CONTENT IS HANDED OVER RATHER THAN FETCHED
 *
 * A nested Studio document cannot fetch. The Studio has no identity of its
 * own; it asks its HOST for a token, posting to `window.parent` at
 * `DASHBOARD_ORIGIN`. Inside the modal its parent is the Studio, not the
 * dashboard, so that request goes nowhere and every call would wait out the
 * token timeout. Rather than widen the auth layer, the frame does not
 * authenticate at all: the gallery already holds the exact payload, tokens and
 * nav it was rendering, and posts them in. Same content as before, by
 * construction — and a frame with no token cannot leak one.
 *
 * THE FRAME MODE IS SAME-ORIGIN ONLY
 *
 * `/preview?frame=device` opened by any other parent — the dashboard's own
 * step-5 iframe, a tab — is the ordinary token-backed `/preview`. A
 * cross-origin parent cannot be read, which is exactly the test.
 *
 * Protocol (both directions `targetOrigin = location.origin`, and each side
 * checks `event.origin` AND `event.source` before reading `event.data`):
 *
 *   frame  → parent  `studio:device-preview:ready`    — listening; send content
 *   parent → frame   `studio:device-preview:render`   — the content (re-sent on change)
 *   frame  → parent  `studio:device-preview:painted`  — first paint with fonts
 */
import type {
  DeepPartial,
  ThemeConfig,
  ThemeTokens,
} from "@smartworkstechnology/storefront-renderer";

/** Query param + value that put `/preview` into device-frame mode. */
export const DEVICE_FRAME_PARAM = "frame";
export const DEVICE_FRAME_VALUE = "device";

export const DEVICE_FRAME_READY = "studio:device-preview:ready";
export const DEVICE_FRAME_RENDER = "studio:device-preview:render";
export const DEVICE_FRAME_PAINTED = "studio:device-preview:painted";

/** Nav link as the preview canvas takes it — plain, structured-cloneable data. */
export interface DeviceFrameNavLink {
  label: string;
  href: string;
  menu?: unknown;
}

/** Everything the frame renders. All of it survives structured clone. */
export interface DeviceFrameContent {
  payload: ThemeConfig;
  themeTokens?: DeepPartial<ThemeTokens>;
  navLinks?: DeviceFrameNavLink[];
  currency?: string;
}

export interface DeviceFrameRenderMessage {
  type: typeof DEVICE_FRAME_RENDER;
  content: DeviceFrameContent;
}

/** The URL the gallery frames. */
export function devicePreviewFrameUrl(): string {
  return `/preview?${DEVICE_FRAME_PARAM}=${DEVICE_FRAME_VALUE}`;
}

/**
 * Is THIS document the gallery's device frame?
 *
 * Needs all three: the `/preview` path, the param, and a parent we can read —
 * which only a same-origin parent allows. Reading a cross-origin parent's
 * `location.origin` throws, and that is the answer.
 */
export function isDevicePreviewFrame(
  win: Window = window,
): boolean {
  if (win.parent === win) return false;
  if (win.location.pathname !== "/preview") return false;
  const params = new URLSearchParams(win.location.search);
  if (params.get(DEVICE_FRAME_PARAM) !== DEVICE_FRAME_VALUE) return false;
  try {
    return win.parent.location.origin === win.location.origin;
  } catch {
    return false;
  }
}

/** A message from `expectedSource`, on our own origin, of type `type`. */
export function isFrameMessage(
  event: MessageEvent,
  expectedSource: MessageEventSource | null | undefined,
  type: string,
): boolean {
  if (event.origin !== window.location.origin) return false;
  if (!expectedSource || event.source !== expectedSource) return false;
  const data = event.data as { type?: unknown } | null;
  return !!data && typeof data === "object" && data.type === type;
}
